import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { fetchProjects } from '../../../fetchProjects';

const API_BASE_URL = 'https://spaceportfolio-api.rajansudhan0.workers.dev';

const ProjectManager = ({ adminKey }) => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtering and Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async (forceRefresh = false) => {
    setLoading(true);
    const data = await fetchProjects(forceRefresh, adminKey);
    setProjects(data);
    
    // Extract unique categories for the dropdown filter
    const uniqueCats = new Set(data.map(p => p.type).filter(Boolean));
    setCategories(Array.from(uniqueCats));
    
    setLoading(false);
  };

  // --- CRUD Operations ---

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setStatusMessage('');

    const formData = new FormData(e.target);
    
    // Helper to parse comma separated lists into arrays
    const parseList = (str) => str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];

    const projectData = {
      id: editingProject?.id || undefined,
      title: formData.get('title'),
      type: formData.get('type'),
      description: formData.get('description'),
      detailedContent: formData.get('detailedContent'),
      image: formData.get('image'),
      liveUrl: formData.get('liveUrl'),
      codeUrl: formData.get('codeUrl'),
      author: formData.get('author') || 'Mr. Sudhan R',
      completionDate: formData.get('completionDate'),
      client: formData.get('client'),
      year: formData.get('year'),
      duration: formData.get('duration'),
      industry: parseList(formData.get('industry')),
      team: parseList(formData.get('team')),
      toolsUsed: parseList(formData.get('toolsUsed')),
      services: parseList(formData.get('services')),
      results: parseList(formData.get('results')),
      featured: formData.get('featured') === 'on' ? 1 : 0,
      metric: formData.get('metric') // Can be string or JSON string, passing raw for now
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/projects`, {
        method: editingProject ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': adminKey
        },
        body: JSON.stringify(projectData)
      });

      if (!res.ok) throw new Error('Failed to save project');
      
      setStatusMessage({ type: 'success', text: 'Project saved successfully!' });
      loadProjects(true);
      
      setTimeout(() => {
        setIsModalOpen(false);
        setStatusMessage('');
      }, 1500);

    } catch (err) {
      console.error(err);
      setStatusMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey }
      });
      if (!res.ok) throw new Error('Failed to delete project');
      loadProjects(true);
    } catch (err) {
      alert(err.message);
    }
  };

  const openAddModal = () => {
    setEditingProject(null);
    setStatusMessage('');
    setIsModalOpen(true);
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setStatusMessage('');
    setIsModalOpen(true);
  };

  // --- Filtering Logic ---
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          project.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCat = selectedCategory === 'All Categories' || project.type === selectedCategory;
    return matchesSearch && matchesCat;
  });

  if (loading) return <div className="h-64 bg-white/5 rounded-2xl animate-pulse"></div>;

  return (
    <div className="space-y-6 pb-12">
      
      {/* Top Action Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-8">
        
        {/* Search & Filter */}
        <div className="flex w-full sm:w-auto gap-4">
          <div className="relative w-full sm:w-64">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input 
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-surface/50 border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-text-primary focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-smooth"
            />
          </div>

          <div className="relative w-full sm:w-48">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full bg-surface/50 border border-white/10 rounded-xl py-2.5 px-4 text-text-primary appearance-none focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-smooth cursor-pointer"
            >
              <option value="All Categories">All Categories</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <Icon name="ChevronDown" size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center w-full sm:w-auto gap-3">
          {/* Mock Document icons from design */}
          <button className="w-10 h-10 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center hover:bg-red-500/20 transition-smooth border border-red-500/20" aria-label="Export PDF">
            <Icon name="FileText" size={18} />
          </button>
          <button className="w-10 h-10 rounded-xl bg-green-500/10 text-green-500 flex items-center justify-center hover:bg-green-500/20 transition-smooth border border-green-500/20" aria-label="Export Spreadsheet">
            <Icon name="Database" size={18} />
          </button>
          <button className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center hover:bg-blue-500/20 transition-smooth border border-blue-500/20 hidden sm:flex" aria-label="Copy Data">
            <Icon name="Copy" size={18} />
          </button>
          
          <button 
            onClick={openAddModal}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-accent hover:bg-accent-600 text-white px-5 py-2.5 rounded-xl font-semibold transition-smooth shadow-lg hover:shadow-accent/25"
          >
            <Icon name="Plus" size={18} />
            Add Project
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-surface/50 border border-white/10 rounded-2xl overflow-x-auto shadow-2xl">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10 text-sm font-semibold text-text-muted">
              <th className="p-4 w-12 text-center">
                <input type="checkbox" className="rounded-sm bg-white/10 border-white/20 text-accent focus:ring-accent/50" />
              </th>
              <th className="p-4">Title</th>
              <th className="p-4">Category</th>
              <th className="p-4">Team / Author</th>
              <th className="p-4">Date</th>
              <th className="p-4 text-center">Featured</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="text-text-primary text-sm divide-y divide-white/5">
            {filteredProjects.length === 0 ? (
               <tr><td colSpan="7" className="p-8 text-center text-text-muted">No projects found.</td></tr>
            ) : (
                filteredProjects.map((project) => (
                <tr key={project.id} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="p-4 w-12 text-center">
                    <input type="checkbox" className="rounded-sm bg-white/10 border-white/20 text-accent focus:ring-accent/50" />
                    </td>
                    <td className="p-4 font-medium flex items-center gap-2">
                    {project.featured === 1 && <Icon name="Star" size={14} className="text-amber-400 fill-current" />}
                    {project.title}
                    </td>
                    <td className="p-4">
                    <span className="bg-white/5 border border-white/10 px-2.5 py-1 rounded-md text-xs text-text-secondary whitespace-nowrap">
                        {project.type || 'N/A'}
                    </span>
                    </td>
                    <td className="p-4 text-text-secondary">
                        {Array.isArray(project.team) && project.team.length > 0 ? `By ${project.team[0]}${project.team.length > 1 ? ' +'+(project.team.length-1) : ''}` : project.author}
                    </td>
                    <td className="p-4 text-text-secondary whitespace-nowrap">
                        {project.completionDate || project.year || 'N/A'}
                    </td>
                    <td className="p-4 text-center">
                    {project.featured === 1 ? (
                        <Icon name="Star" size={16} className="text-amber-400 fill-current mx-auto" />
                    ) : (
                        <span className="text-text-muted">-</span>
                    )}
                    </td>
                    <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                            onClick={() => openEditModal(project)}
                            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-text-secondary hover:text-white flex items-center justify-center transition-smooth"
                            title="Edit"
                        >
                            <Icon name="Edit2" size={14} />
                        </button>
                        <button 
                            onClick={() => handleDelete(project.id)}
                            className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center transition-smooth border border-transparent hover:border-red-500/30"
                            title="Delete"
                        >
                            <Icon name="Trash2" size={14} />
                        </button>
                    </div>
                    </td>
                </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

       {/* Project Form Modal */}
       {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pt-20 pb-4">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => !isSaving && setIsModalOpen(false)}></div>
          <div className="bg-surface border border-white/10 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto relative z-10 shadow-3xl">
            <div className="sticky top-0 bg-surface/90 backdrop-blur-md px-6 py-4 border-b border-white/10 flex justify-between items-center z-20">
              <h3 className="font-heading font-bold text-xl text-text-primary">
                {editingProject ? 'Edit Project' : 'Add New Project'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-text-muted hover:text-white transition-smooth">
                <Icon name="X" size={24} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-8">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-heading font-semibold text-accent text-sm uppercase tracking-wider">Basic Information</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Title *</label>
                    <input type="text" name="title" required defaultValue={editingProject?.title} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent focus:ring-1 focus:ring-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Category / Type *</label>
                    <input type="text" name="type" required defaultValue={editingProject?.type} placeholder="e.g. Remote Sensing, Full Stack" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent focus:ring-1 focus:ring-accent" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Short Description *</label>
                  <textarea name="description" required rows={2} defaultValue={editingProject?.description} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent focus:ring-1 focus:ring-accent resize-none"></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Detailed Content</label>
                  <textarea name="detailedContent" rows={6} defaultValue={editingProject?.detailedContent} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent focus:ring-1 focus:ring-accent font-mono text-sm"></textarea>
                </div>
              </div>

              {/* Media & Links */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <h4 className="font-heading font-semibold text-accent text-sm uppercase tracking-wider">Media & Links</h4>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Image URL</label>
                  <input type="text" name="image" defaultValue={editingProject?.image} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent focus:ring-1 focus:ring-accent" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Live Demo URL</label>
                    <input type="url" name="liveUrl" defaultValue={editingProject?.liveUrl} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent focus:ring-1 focus:ring-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Source Code URL</label>
                    <input type="url" name="codeUrl" defaultValue={editingProject?.codeUrl} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent focus:ring-1 focus:ring-accent" />
                  </div>
                </div>
              </div>

              {/* Taxonomy & Meta */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                 <h4 className="font-heading font-semibold text-accent text-sm uppercase tracking-wider">Taxonomy & Meta (Comma separated)</h4>
                 <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Tools Used</label>
                        <input type="text" name="toolsUsed" defaultValue={editingProject?.toolsUsed?.join(', ')} placeholder="React, Node.js, QGIS..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Industry / Tags</label>
                        <input type="text" name="industry" defaultValue={editingProject?.industry?.join(', ')} placeholder="Healthcare, EdTech..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Team Members</label>
                        <input type="text" name="team" defaultValue={editingProject?.team?.join(', ')} placeholder="John Doe, Jane Smith" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Services</label>
                        <input type="text" name="services" defaultValue={editingProject?.services?.join(', ')} placeholder="Web Dev, Full Stack..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Key Results</label>
                        <input type="text" name="results" defaultValue={editingProject?.results?.join(', ')} placeholder="20% faster load, 50k users..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                    </div>
                 </div>
              </div>

              {/* Details & Settings */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <h4 className="font-heading font-semibold text-accent text-sm uppercase tracking-wider">Details & Settings</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Author</label>
                    <input type="text" name="author" defaultValue={editingProject?.author || 'Mr. Sudhan R'} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Client / Org</label>
                    <input type="text" name="client" defaultValue={editingProject?.client} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Completion Date</label>
                    <input type="text" name="completionDate" defaultValue={editingProject?.completionDate} placeholder="e.g. Oct 2024" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Year</label>
                    <input type="text" name="year" defaultValue={editingProject?.year} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Duration</label>
                    <input type="text" name="duration" defaultValue={editingProject?.duration} placeholder="e.g. 3 Months" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Featured Project?</label>
                    <div className="h-10 flex items-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" name="featured" defaultChecked={editingProject?.featured === 1} className="sr-only peer" />
                            <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-white/50 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent ring-1 ring-white/10"></div>
                            <span className="ml-3 text-sm font-medium text-text-secondary">Show on Home</span>
                        </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status & Submit */}
              <div className="flex items-center justify-between pt-6 mt-6 border-t border-white/10 sticky bottom-0 bg-surface/90 backdrop-blur-md p-4 -mx-6 -mb-6 rounded-b-2xl z-20">
                <div className="text-sm font-medium">
                  {statusMessage && (
                    <span className={statusMessage.type === 'success' ? 'text-success' : 'text-error'}>
                      {statusMessage.text}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 rounded-xl text-text-primary bg-white/5 hover:bg-white/10 transition-smooth font-medium">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSaving} className="px-5 py-2.5 rounded-xl text-white bg-accent hover:bg-accent-600 disabled:opacity-50 transition-smooth font-semibold flex items-center gap-2">
                    {isSaving ? (
                      <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Saving...</>
                    ) : (
                      <><Icon name="Save" size={18} /> Save Project</>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProjectManager;
